import React, { useState, Fragment, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Switch from 'react-switch';
import { useTransition } from 'react-spring';
import { Formik, ErrorMessage, Field } from 'formik';

import { Creators as configActions } from '../../../store/ducks/config-section';
import { Creators as toastActions } from '../../../store/ducks/toast';
import { CircularProgressIndicator } from '../../../components-reactjs/CircularProgressIndicator';

import { OPTIONS_DIAS, OPTIONS_INTERVALOS } from './options';

import { ErrorText } from '../../../components-reactjs/ErrorText';
import { getCfg, saveCfg } from '../api';

import Colors from '../../../config/Colors';
import { TIPO_ENTREGA_RETIRADA } from '../../../constants/configuracao-horarios';
import { configHorarioSchema } from '../../../constants/schemas';
import { Button } from '../../../components-reactjs/Button';
import { CurrencyInput } from '../../../components-reactjs/CurrencyInput';
import { InputText } from '../../../components-reactjs/InputText';
import { Select } from '../../../components-reactjs/Select';
import { SectionTitles, RadioInput, RadioRow, Descricao } from '../styles';

import {
    SwitchContainer,
    SwitchText,
    ScrollConfig,
    Form,
    FieldSet,
    TempoPreparo,
    TempoPreparoRow,
    TipoPreparoInputContainer,
    ValorMinimo,
    ValorMinimoRow,
    ValorMinimoInputContainer,
    Agendamento,
    AgendamentoContent,
    AgendamentoTipo,
    AgendamentoTipoRow,
    AgendamentoCheckBoxButton,
    AgendamentoCheckBox,
    AgendamentoLimite,
    AgendamentoLimiteDescricao,
    Horario,
    HorarioContent,
    HorarioInterval,
    HorarioSubTitle,
    HorarioPeriod,
    Footer
} from './styles';

const EntregaRetirada = ({ idTipoEntregaRetirada, addToast }) => {
    const [cfg, setCfg] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const onSubmit = async (values, { resetForm }) => {
        const {
            fgAtivo,
            nrMinutoIntervalo,
            nrDiasAceitaPedido,
            nrMinutoPreparo,
            vlPedidoMinimo,
            allowSchedule,
            hasInterval
        } = values;

        let valueOfVlPedidoMinimo = vlPedidoMinimo;
        if (typeof vlPedidoMinimo === 'string') {
            valueOfVlPedidoMinimo = vlPedidoMinimo.replace(',', '.');
        }

        const data = {
            idTipoEntregaRetirada,
            fgAtivo,
            nrMinutoPreparo,
            nrDiasAceitaPedido: allowSchedule ? nrDiasAceitaPedido.value : 0,
            nrMinutoIntervalo: hasInterval ? nrMinutoIntervalo.value : null,
            vlPedidoMinimo: valueOfVlPedidoMinimo
                ? parseFloat(valueOfVlPedidoMinimo)
                : null
        };

        try {
            const response = await saveCfg(data);
            if (response.status >= 400) {
                throw Error(response.data.errorMessage);
            }

            if (hasInterval) {
                data.nrMinutoIntervalo = values.nrMinutoIntervalo;
                data.hasInterval = true;
            } else {
                data.nrMinutoIntervalo = null;
            }

            if (allowSchedule) {
                data.nrDiasAceitaPedido = values.nrDiasAceitaPedido;
                data.allowSchedule = true;
            } else {
                data.allowSchedule = false;
            }

            resetForm({ values: { ...data } });

            addToast({
                text: 'Alterações salvas com sucesso',
                theme: 'success'
            });
        } catch (error) {
            addToast({
                text: error.message || 'Não foi possível salvar as alterações',
                theme: 'error'
            });
        }
    };

    const validateConfigHorario = async (values) => {
        try {
            await configHorarioSchema.validate(values, {
                abortEarly: false
            });
            return {};
        } catch (validationError) {
            return validationError.inner.reduce((errors, error) => {
                errors[error.path] = error.message;
                return errors;
            }, {});
        }
    };

    useEffect(() => {
        setIsLoading(true);

        getCfg(idTipoEntregaRetirada)
            .then((cfg) => {
                if (cfg.nrDiasAceitaPedido) {
                    cfg.allowSchedule = true;
                    cfg.nrDiasAceitaPedido = OPTIONS_DIAS.find(
                        (option) => option.value === cfg.nrDiasAceitaPedido
                    );
                } else {
                    cfg.allowSchedule = false;
                    cfg.nrDiasAceitaPedido = OPTIONS_DIAS[0];
                }
                if (cfg.nrMinutoIntervalo) {
                    cfg.hasInterval = true;
                    cfg.nrMinutoIntervalo = OPTIONS_INTERVALOS.find(
                        (option) => option.value === cfg.nrMinutoIntervalo
                    );
                } else {
                    cfg.hasInterval = false;
                    cfg.nrMinutoIntervalo = OPTIONS_INTERVALOS[0];
                }

                setCfg(cfg);
                setIsLoading(false);
            })
            .catch((error) => {
                const { errorMessage } = error;
                setIsLoading(false);
                addToast({
                    text: errorMessage || error,
                    theme: 'error'
                });
            });
    }, [idTipoEntregaRetirada]);

    return isLoading ? (
        <CircularProgressIndicator />
    ) : (
        <Fragment>
            <Formik
                initialValues={cfg}
                validate={(values) =>
                    values.fgAtivo ? validateConfigHorario(values) : {}
                }
                validateOnChange={false}
                validateOnBlur={true}
                onSubmit={onSubmit}
            >
                {({
                    setFieldValue,
                    setErrors,
                    values,
                    errors,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                    dirty
                }) => {
                    const transitionCfg = {
                        initial: { x: 0, y: -15, opacity: 0, delay: 0 },
                        from: { x: 0, y: -15, opacity: 0, delay: 0 },
                        enter: { x: 0, y: 0, opacity: 1, delay: 0 },
                        leave: { x: 0, y: -15, opacity: 0, delay: 0 }
                    };

                    const scheduleOrder = useTransition(values.allowSchedule, {
                        config: { duration: values.allowSchedule ? 150 : 50 },
                        ...transitionCfg
                    });

                    const transitionPeriod = useTransition(values.hasInterval, {
                        config: { duration: values.hasInterval ? 150 : 50 },
                        ...transitionCfg
                    });
                    return (
                        <Fragment>
                            <ScrollConfig>
                                <Form
                                    autoComplete="off"
                                    onSubmit={handleSubmit}
                                    id="delivery-form"
                                >
                                    <SwitchContainer>
                                        <Switch
                                            name="fgAtivo"
                                            onChange={(fgAtivoValue) => {
                                                setFieldValue(
                                                    'fgAtivo',
                                                    fgAtivoValue
                                                );
                                                if (!fgAtivoValue) {
                                                    setErrors({});
                                                }
                                            }}
                                            checked={values.fgAtivo || false}
                                            height={20}
                                            width={40}
                                            onColor={Colors.blue_button}
                                            checkedIcon={false}
                                            uncheckedIcon={false}
                                        />
                                        <SwitchText>
                                            {' '}
                                            Habilitar{' '}
                                            {idTipoEntregaRetirada ===
                                            TIPO_ENTREGA_RETIRADA.RETIRADA
                                                ? 'retirada'
                                                : 'entrega'}
                                        </SwitchText>
                                    </SwitchContainer>
                                    <FieldSet disabled={!values.fgAtivo}>
                                        <TempoPreparo>
                                            <SectionTitles>
                                                Tempo de Preparo
                                            </SectionTitles>
                                            <TempoPreparoRow>
                                                <TipoPreparoInputContainer>
                                                    <Descricao htmlFor="nrMinutoPreparo">
                                                        Tempo de Preparo (
                                                        {idTipoEntregaRetirada ===
                                                        TIPO_ENTREGA_RETIRADA.RETIRADA
                                                            ? 'retirada'
                                                            : 'entrega'}
                                                        ) em minutos
                                                    </Descricao>
                                                    <InputText
                                                        $hasAnError={
                                                            !!errors.nrMinutoPreparo
                                                        }
                                                        id="nrMinutoPreparo"
                                                        name="nrMinutoPreparo"
                                                        onChange={(e) => {
                                                            if (
                                                                /^[0-9]*$/.test(
                                                                    e.target
                                                                        .value
                                                                )
                                                            ) {
                                                                setFieldValue(
                                                                    'nrMinutoPreparo',
                                                                    Number(
                                                                        e.target
                                                                            .value
                                                                    )
                                                                );
                                                            }
                                                            if (
                                                                !e.target.value
                                                            ) {
                                                                setFieldValue(
                                                                    'nrMinutoPreparo',
                                                                    ''
                                                                );
                                                            }
                                                        }}
                                                        value={
                                                            values.nrMinutoPreparo
                                                        }
                                                        onBlur={handleBlur}
                                                        style={{
                                                            height: 36,
                                                            maxWidth: 220
                                                        }}
                                                    />
                                                    <ErrorMessage
                                                        component={ErrorText}
                                                        name="nrMinutoPreparo"
                                                        message={
                                                            errors.nrMinutoPreparo
                                                        }
                                                    />
                                                </TipoPreparoInputContainer>
                                            </TempoPreparoRow>
                                        </TempoPreparo>

                                        <ValorMinimo>
                                            <SectionTitles>
                                                Valor Mínimo
                                            </SectionTitles>
                                            <ValorMinimoRow>
                                                <ValorMinimoInputContainer>
                                                    <Descricao>
                                                        Valor Mínimo(
                                                        {idTipoEntregaRetirada ===
                                                        TIPO_ENTREGA_RETIRADA.RETIRADA
                                                            ? 'retirada'
                                                            : 'entrega'}
                                                        )
                                                    </Descricao>
                                                    <Field name="vlPedidoMinimo">
                                                        {({ field, meta }) => {
                                                            return (
                                                                <Fragment>
                                                                    <CurrencyInput
                                                                        $hasAnError={
                                                                            !!errors.vlPedidoMinimo
                                                                        }
                                                                        onValueChange={(
                                                                            value
                                                                        ) =>
                                                                            setFieldValue(
                                                                                'vlPedidoMinimo',
                                                                                value
                                                                            )
                                                                        }
                                                                        onBlur={
                                                                            field.onBlur
                                                                        }
                                                                        value={
                                                                            values.vlPedidoMinimo
                                                                        }
                                                                        allowNegativeValue={
                                                                            false
                                                                        }
                                                                        style={{
                                                                            height: 36,
                                                                            maxWidth: 220
                                                                        }}
                                                                    />
                                                                    {!!meta.error && (
                                                                        <ErrorText
                                                                            message={
                                                                                meta.error
                                                                            }
                                                                        />
                                                                    )}
                                                                </Fragment>
                                                            );
                                                        }}
                                                    </Field>
                                                </ValorMinimoInputContainer>
                                            </ValorMinimoRow>
                                        </ValorMinimo>

                                        <Agendamento>
                                            <SectionTitles>
                                                Agendamento
                                            </SectionTitles>
                                            <AgendamentoContent>
                                                <AgendamentoTipo>
                                                    <AgendamentoCheckBoxButton
                                                        type="button"
                                                        onClick={() => {
                                                            setFieldValue(
                                                                'allowSchedule',
                                                                !values.allowSchedule
                                                            );
                                                            setCfg({
                                                                ...cfg,
                                                                allowSchedule:
                                                                    !values.allowSchedule
                                                            });
                                                        }}
                                                    >
                                                        <AgendamentoTipoRow>
                                                            <AgendamentoCheckBox
                                                                name="allowSchedule"
                                                                type="checkbox"
                                                                checked={
                                                                    values.allowSchedule ||
                                                                    false
                                                                }
                                                                onChange={() => {
                                                                    setFieldValue(
                                                                        'allowSchedule',
                                                                        !values.allowSchedule
                                                                    );
                                                                    setCfg({
                                                                        ...cfg,
                                                                        allowSchedule:
                                                                            !values.allowSchedule
                                                                    });
                                                                }}
                                                            />
                                                            Permitir agendar{' '}
                                                            {idTipoEntregaRetirada ===
                                                            TIPO_ENTREGA_RETIRADA.RETIRADA
                                                                ? 'retirada'
                                                                : 'entrega'}
                                                        </AgendamentoTipoRow>
                                                    </AgendamentoCheckBoxButton>

                                                    {scheduleOrder(
                                                        (
                                                            style,
                                                            allowSchedule
                                                        ) =>
                                                            allowSchedule &&
                                                            values.allowSchedule && (
                                                                <AgendamentoLimite
                                                                    style={
                                                                        style
                                                                    }
                                                                >
                                                                    <AgendamentoLimiteDescricao>
                                                                        Limite
                                                                        de
                                                                        agendamento
                                                                    </AgendamentoLimiteDescricao>
                                                                    <Select
                                                                        name="nrDiasAceitaPedido"
                                                                        menuPlacement="auto"
                                                                        selectStyles={{
                                                                            width: 250
                                                                        }}
                                                                        value={
                                                                            OPTIONS_DIAS
                                                                                ? OPTIONS_DIAS.find(
                                                                                      (
                                                                                          option
                                                                                      ) =>
                                                                                          option.value ===
                                                                                          values
                                                                                              .nrDiasAceitaPedido
                                                                                              .value
                                                                                  )
                                                                                : ''
                                                                        }
                                                                        onChange={(
                                                                            option
                                                                        ) => {
                                                                            setFieldValue(
                                                                                'nrDiasAceitaPedido',
                                                                                option
                                                                            );
                                                                        }}
                                                                        options={
                                                                            OPTIONS_DIAS
                                                                        }
                                                                    />
                                                                </AgendamentoLimite>
                                                            )
                                                    )}
                                                </AgendamentoTipo>
                                            </AgendamentoContent>
                                        </Agendamento>

                                        <Horario>
                                            <HorarioContent>
                                                <SectionTitles>
                                                    Horários para{' '}
                                                    {idTipoEntregaRetirada ===
                                                    TIPO_ENTREGA_RETIRADA.RETIRADA
                                                        ? 'retirada'
                                                        : 'entrega'}
                                                </SectionTitles>
                                                <HorarioSubTitle>
                                                    Em quais horários os pedidos
                                                    poderão ser{' '}
                                                    {idTipoEntregaRetirada ===
                                                    TIPO_ENTREGA_RETIRADA.RETIRADA
                                                        ? 'retirados'
                                                        : 'entregues'}
                                                    ?
                                                </HorarioSubTitle>

                                                <RadioRow
                                                    onClick={() => {
                                                        setFieldValue(
                                                            'hasInterval',
                                                            false
                                                        );
                                                    }}
                                                >
                                                    <RadioInput
                                                        name="hasInterval"
                                                        value={false}
                                                        checked={Boolean(
                                                            !values.hasInterval
                                                        )}
                                                    />
                                                    Durante todo o período de
                                                    funcionamento (Ex: das 8h às
                                                    18h)
                                                </RadioRow>

                                                <HorarioInterval className="horario-interval">
                                                    <RadioRow
                                                        onClick={() => {
                                                            setFieldValue(
                                                                'nrMinutoIntervalo',
                                                                OPTIONS_INTERVALOS[0]
                                                            );
                                                            setFieldValue(
                                                                'hasInterval',
                                                                true
                                                            );
                                                        }}
                                                    >
                                                        <RadioInput
                                                            name="hasInterval"
                                                            value={true}
                                                            checked={Boolean(
                                                                values.hasInterval
                                                            )}
                                                        />
                                                        Definir intervalo (Ex: a
                                                        cada 30minutos)
                                                    </RadioRow>

                                                    {transitionPeriod(
                                                        (style, hasInterval) =>
                                                            hasInterval &&
                                                            values.hasInterval && (
                                                                <HorarioPeriod
                                                                    style={
                                                                        style
                                                                    }
                                                                >
                                                                    <Descricao>
                                                                        Intervalo
                                                                    </Descricao>
                                                                    <Select
                                                                        menuPortalTarget={
                                                                            document.body
                                                                        }
                                                                        name="nrMinutoIntervalo"
                                                                        menuPlacement="auto"
                                                                        selectStyles={{
                                                                            width: 250
                                                                        }}
                                                                        value={
                                                                            OPTIONS_INTERVALOS
                                                                                ? OPTIONS_INTERVALOS.find(
                                                                                      (
                                                                                          option
                                                                                      ) =>
                                                                                          option.value ===
                                                                                          values
                                                                                              .nrMinutoIntervalo
                                                                                              .value
                                                                                  )
                                                                                : ''
                                                                        }
                                                                        onChange={(
                                                                            option
                                                                        ) => {
                                                                            setFieldValue(
                                                                                'nrMinutoIntervalo',
                                                                                option
                                                                            );
                                                                        }}
                                                                        options={
                                                                            OPTIONS_INTERVALOS
                                                                        }
                                                                    />
                                                                </HorarioPeriod>
                                                            )
                                                    )}
                                                </HorarioInterval>
                                            </HorarioContent>
                                        </Horario>
                                    </FieldSet>
                                </Form>
                            </ScrollConfig>
                            {dirty && (
                                <Footer>
                                    <Button
                                        disabled={isSubmitting}
                                        style={{
                                            fontSize: '1.5rem',
                                            fontFamile: 'NunitoSans-Regular',
                                            maxHeight: 30
                                        }}
                                        title={'Salvar Alterações'}
                                        type="submit"
                                        form="delivery-form"
                                    />
                                </Footer>
                            )}
                        </Fragment>
                    );
                }}
            </Formik>
        </Fragment>
    );
};

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            ...toastActions,
            ...configActions
        },
        dispatch
    );

export default connect(null, mapDispatchToProps)(EntregaRetirada);
